import {gql} from '@apollo/client';
import {compact} from '@benchling/js-utils';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {isWellSample, SampleDetails} from 'constants/SampleConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible as isInaccessibleGQL} from 'graphql-client/InaccessibleInfo';
import getSampleId from 'notebook/sample-propagation/util/getSampleId';
import {
  makeBioentitySampleDetails,
  makeBoxSampleDetails,
  makeContainerSampleDetails,
  makePlateSampleDetails,
  makeWellSampleDetails,
} from 'notebook/sample-propagation/util/makeSampleDetails';
import APIUtil from 'util/APIUtil';

import {SourceSampleSelectionTableQuery} from './__generated/SourceSampleSelectionTableQuery';

/**
 * This hook is used only in the Add Samples modal, bt the SourceSampleSelectionTable.
 * Given a list of samples, it fetches the data needed to show the samples to the user.
 */

export const SOURCE_SAMPLE_SELECTION_TABLE_QUERY = gql`
  query SourceSampleSelectionTableQuery(
    $bioentityIds: [StrictString!]!
    $boxIds: [StrictString!]!
    $containerIds: [StrictString!]!
    $plateIds: [StrictString!]!
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    bioentities(ids: $bioentityIds) {
      ...BioentitySummaryOrInaccessibleGraphQL
    }
    boxes(ids: $boxIds) {
      ... on BoxNode {
        ...BoxSummaryGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    containers(ids: $containerIds) {
      ... on ContainerNode {
        ...ContainerGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    plates(ids: $plateIds) {
      ... on PlateNode {
        ...PlateSummaryGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('BoxSummary').fragment}
  ${getFragmentWrapper('Container').fragment}
  ${getFragmentWrapper('PlateSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface SampleSelectionTableData {
  status: QueryResultType;
  samplesByIndex: Array<SampleDetails | null>;
  archivedIds: Array<string>;
  inaccessibleIds: Array<string>;
}

function isArchived(archiveRecord: ArchiveRecord | null): boolean {
  return archiveRecord != null;
}

export default function useSampleQueryData(samples: Array<SampleDetails>): SampleSelectionTableData {
  // Sample IDs are needed for hydrating data from the server. However, we use the `samples` param
  // as the master list (e.g., for ensuring we return data in the same order that the user gave us)
  const uniqueSampleIds = compact(_.unique(samples.map((sample) => getSampleId(sample))));
  const sourceSamplesQueryResult = useBenchlingQuery<SourceSampleSelectionTableQuery>(
    SOURCE_SAMPLE_SELECTION_TABLE_QUERY,
    {
      variables: {
        bioentityIds: uniqueSampleIds.filter((id) => APIUtil.isBiologicalEntity(id)),
        boxIds: uniqueSampleIds.filter((id) => APIUtil.isBox(id)),
        containerIds: uniqueSampleIds.filter((id) => APIUtil.isContainer(id)),
        plateIds: uniqueSampleIds.filter((id) => APIUtil.isPlate(id)),
      },
    }
  );

  if (sourceSamplesQueryResult.type !== QueryResultType.SUCCESS) {
    return {
      status: sourceSamplesQueryResult.type,
      samplesByIndex: [],
      archivedIds: [],
      inaccessibleIds: [],
    };
  }

  const samplesByIndex: Array<SampleDetails | null> = [];
  const archivedIds: Array<string> = [];
  const inaccessibleIds: Array<string> = [];

  // For each sample we received from the caller, record its server response into the master "samplesByKey"
  // object. Any sample which is inaccessible or archived will be filtered from that object, and reported
  // in a separate list of archived/inaccessible ids.
  for (let index = 0; index < samples.length; index++) {
    const sample = samples[index];
    const sampleId = getSampleId(sample);
    if (sampleId != null) {
      if (APIUtil.isBiologicalEntity(sampleId)) {
        recordOneBioentityResult(sample, sampleId, index, sourceSamplesQueryResult.data.bioentities);
      }
      if (APIUtil.isContainer(sampleId)) {
        recordOneContainerResult(sample, sampleId, index, sourceSamplesQueryResult.data.containers);
      }
      if (APIUtil.isBox(sampleId)) {
        recordOneBoxResult(sample, sampleId, index, sourceSamplesQueryResult.data.boxes);
      }
      // Both plates and wells will have a sampleId that refers to the plate. They are handled
      // differently in the recording function below.
      if (APIUtil.isPlate(sampleId)) {
        recordOnePlateOrWellResult(sample, sampleId, index, sourceSamplesQueryResult.data.plates);
      }
      if (sample.textInfo != null || sample.numberInfo != null || sample.booleanInfo != null) {
        samplesByIndex[index] = sample;
      }
    } else {
      samplesByIndex[index] = null;
    }
  }
  return {samplesByIndex, archivedIds, inaccessibleIds, status: QueryResultType.SUCCESS};

  // These functions were extracted from the loop above for clarity
  function recordOneBioentityResult(
    sample: SampleDetails,
    sampleId: string,
    sampleIndex: number,
    allServerBioentities: SourceSampleSelectionTableQuery['bioentities']
  ): void {
    const bioentity = allServerBioentities.find((entity) =>
      isInaccessibleGQL(entity) ? entity.invalidId === sampleId : entity.id === sampleId
    );
    if (bioentity == null) {
      return;
    }
    if (isInaccessibleGQL(bioentity)) {
      inaccessibleIds.push(sampleId);
    } else {
      const sampleFromServer = graphQLToREST('BioentitySummary', bioentity);
      if (isArchived(sampleFromServer.archiveRecord)) {
        archivedIds.push(sampleId);
      } else {
        samplesByIndex[sampleIndex] = makeBioentitySampleDetails(sampleFromServer, sample.error);
      }
    }
  }

  function recordOneBoxResult(
    sample: SampleDetails,
    sampleId: string,
    sampleIndex: number,
    allServerBoxes: SourceSampleSelectionTableQuery['boxes']
  ): void {
    const box = allServerBoxes.find((b) =>
      isInaccessibleGQL(b) ? b.invalidId === sampleId : b.id === sampleId
    );
    if (box == null) {
      return;
    }

    if (isInaccessibleGQL(box)) {
      inaccessibleIds.push(sampleId);
    } else {
      const boxFromServer = graphQLToREST('BoxSummary', box);
      if (isArchived(boxFromServer.archiveRecord)) {
        archivedIds.push(sampleId);
      } else {
        samplesByIndex[sampleIndex] = makeBoxSampleDetails(boxFromServer, sample.error);
      }
    }
  }

  function recordOneContainerResult(
    sample: SampleDetails,
    sampleId: string,
    sampleIndex: number,
    allServerContainers: SourceSampleSelectionTableQuery['containers']
  ): void {
    const container = allServerContainers.find((oneContainer) =>
      isInaccessibleGQL(oneContainer) ? oneContainer.invalidId === sampleId : oneContainer.id === sampleId
    );
    if (container == null) {
      return;
    }

    if (isInaccessibleGQL(container)) {
      inaccessibleIds.push(sampleId);
    } else {
      const containerFromServer = graphQLToREST('Container', container);
      if (isArchived(containerFromServer.archiveRecord)) {
        archivedIds.push(sampleId);
      } else {
        samplesByIndex[sampleIndex] = makeContainerSampleDetails(containerFromServer, sample.error);
      }
    }
  }

  function recordOnePlateOrWellResult(
    sample: SampleDetails,
    sampleId: string,
    sampleIndex: number,
    allServerPlates: SourceSampleSelectionTableQuery['plates']
  ): void {
    const plate = allServerPlates.find((onePlate) =>
      isInaccessibleGQL(onePlate) ? onePlate.invalidId === sampleId : onePlate.id === sampleId
    );
    if (plate == null) {
      return;
    }

    if (isInaccessibleGQL(plate)) {
      inaccessibleIds.push(sampleId);
    } else {
      const plateFromServer = graphQLToREST('PlateSummary', plate);
      if (isArchived(plateFromServer.archiveRecord)) {
        archivedIds.push(sampleId);
      } else {
        // We query the server for the plate data for both PLATE samples and WELL samples
        samplesByIndex[sampleIndex] = isWellSample(sample)
          ? makeWellSampleDetails(plateFromServer, sample.position, sample.error)
          : makePlateSampleDetails(plateFromServer, sample.error);
      }
    }
  }
}
